
import React from "react";
import {
  Box,
  Breadcrumbs as MUIBreadcrumbs,
  Link,
  Typography,
} from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link as RouterLink, useLocation, useMatches } from 'react-router';

const resolveValue = (value, match) =>
  typeof value === "function" ? value(match) : value;

const normalizeCrumb = (crumb, match) => {
  if (!crumb?.label) return null;
  return {
    label: resolveValue(crumb.label, match),
    to: crumb.to ?? match.pathname,
    link: crumb.link !== false,
  };
};

const formatSegment = (segment) =>
  segment
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const PageBreadcrumbs = () => {
  const location = useLocation();
  const matches = useMatches();
  const items = [];

  // Pages that render their own sticky header (search bars, tab strips, etc.)
  // can opt out of the breadcrumbs row entirely by setting
  // `handle.hideCrumbs = true` on their route. Without this escape hatch the
  // ~60px breadcrumbs row sits between the NavBar and the page's own header
  // and reads as wasted vertical space — the sticky element ends up 100+px
  // below the viewport top ("floating in the middle").
  if (matches.some((match) => match.handle?.hideCrumbs)) {
    return null;
  }

  matches.forEach((match) => {
    const handle = match.handle || {};
    if (handle.crumbs) {
      const list = resolveValue(handle.crumbs, match) || [];
      list.forEach((crumb) => {
        const normalized = normalizeCrumb(crumb, match);
        if (normalized?.label) items.push(normalized);
      });
      return;
    }

    if (handle.crumb) {
      const normalized = normalizeCrumb(
        { label: handle.crumb, to: match.pathname, link: handle.link },
        match
      );
      if (normalized?.label) items.push(normalized);
    }
  });

  if (!items.length) {
    const labelMap = {
      "user-home": "Home",
      booking: "Reservation",
      "customer-fees": "Customer Fees",
      "chains": "Hotel Chains",
      "msi-transactions": "Transactions",
      "transactions": "Transactions",
      "derby-status": "Derby Status",
      "my-reservations": "My Reservations",
      billing: "Billing",
    };
    const segments = location.pathname.split("/").filter(Boolean);
    segments.forEach((segment, index) => {
      const label = labelMap[segment] || formatSegment(segment);
      items.push({
        label,
        to: `/${segments.slice(0, index + 1).join("/")}`,
        link: index < segments.length - 1,
      });
    });
  }

  if (!items.length) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <MUIBreadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ color: "text.secondary", fontSize: 13 }}
      >
        <Link
          component={RouterLink}
          to="/user-home"
          underline="none"
          sx={{
            color: "text.secondary",
            display: "inline-flex",
            alignItems: "center"
          }}>
          <HomeRoundedIcon fontSize="small" />
        </Link>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          if (isLast || !item.link) {
            return (
              <Typography key={`${item.label}-${index}`} variant="caption" sx={{
                color: "text.primary"
              }}>
                {item.label}
              </Typography>
            );
          }
          return (
            <Link
              key={`${item.label}-${index}`}
              component={RouterLink}
              to={item.to}
              underline="hover"
              sx={{
                color: "text.secondary",
                fontSize: 13
              }}>
              {item.label}
            </Link>
          );
        })}
      </MUIBreadcrumbs>
    </Box>
  );
};

export default PageBreadcrumbs;
